import React, { useState, useEffect } from "react";
import { Button, Form, Input, AutoComplete, Select } from "antd";
import { toast } from "react-toastify";
import api from "../../../service/api"; // Verifique o caminho para sua API
import EmpresaMultiSelect from "../EmpresaMultiSelect";

// Ajuste a interface do item de categoria se necessário, adicione o id
interface CategoriaExtratoItem {
    id: string;
    categoria: string;
}

// Interface para os valores do formulário
interface FormValues {
    CategoriaId: string;
    Descricao?: string[];
    Pagamentos?: number | null;
    Empresas?: string[];
    excecao?: string[];
}

// Interface para os dados do item de edição, adicione as propriedades
interface CategoriaDetalheItem {
    id: number;
    CategoriaId: string;
    Descricao: string;
    Excecao: string;
    Pagamentos?: number | null;
    Empresas?: string[];
}

// Opção de pagamento (periodicidade) vinda do enum do back-end (GET /CategoriaDetalhe/pagamentos)
interface PagamentoOption {
    value: number;
    label: string;
}

// Empresa do grupo (GET /CategoriaDetalhe/empresas-by-group). O value é o CNPJ.
interface EmpresaOption {
    value: string;
    label: string;
}

// Adicione a prop itemToEdit à interface do componente
interface CategoriaFormProps {
    onSuccess: () => void;
    itemToEdit?: CategoriaDetalheItem | null;
}

const CategoriaForm: React.FC<CategoriaFormProps> = ({ onSuccess, itemToEdit }) => {
    const [form] = Form.useForm<FormValues>();
    const [isLoading, setIsLoading] = useState(false);
    const [categorias, setCategorias] = useState<CategoriaExtratoItem[]>([]);
    const [grupoDoCliente, setGrupoDoCliente] = useState<string | null>(null);
    const [pagamentosOptions, setPagamentosOptions] = useState<PagamentoOption[]>([]);
    const [empresasOptions, setEmpresasOptions] = useState<EmpresaOption[]>([]);

    useEffect(() => {
        api.get<PagamentoOption[]>("/CategoriaDetalhe/pagamentos")
            .then(response => setPagamentosOptions(response.data))
            .catch(() => toast.error("Erro ao carregar as opções de pagamento."));
    }, []);

    useEffect(() => {
        const cliente = localStorage.getItem("cliente");
        if (cliente) {
            try {
                const clienteObj = JSON.parse(cliente);
                if (clienteObj && clienteObj.nomeDoGrupo) {
                    const grupo = clienteObj.nomeDoGrupo;
                    setGrupoDoCliente(grupo);
                    fetchCategorias(grupo);
                    fetchEmpresas(grupo);
                }
            } catch (e) {
                console.error("Erro ao fazer parse do cliente de localStorage:", e);
                toast.error("Erro ao carregar dados do cliente.");
            }
        }
    }, []);

    // **NOVO:** useEffect para carregar os dados de edição
    useEffect(() => {
        if (itemToEdit) {
            // Popula o formulário com os dados do item de edição
            // Converte strings separadas por vírgula em arrays
            form.setFieldsValue({
                CategoriaId: itemToEdit.CategoriaId,
                Descricao: itemToEdit.Descricao ? itemToEdit.Descricao.split(',') : [],
                Pagamentos: itemToEdit.Pagamentos ?? null,
                Empresas: itemToEdit.Empresas ?? [],
                excecao: itemToEdit.Excecao ? itemToEdit.Excecao.split(',') : [],
            });
        } else {
            // Limpa o formulário para um novo cadastro
            form.resetFields();
        }
    }, [itemToEdit, form]);

    const fetchCategorias = async (grupo: string) => {
        try {
            const response = await api.get<CategoriaExtratoItem[]>("/CategoriaExtrato/get-all-category-by-group", {
                params: { group: grupo },
            });
            setCategorias(response.data);
        } catch (error) {
            toast.error("Erro ao carregar categorias.");
            console.error("Erro ao carregar categorias:", error);
        }
    };

    const fetchEmpresas = async (grupo: string) => {
        try {
            const response = await api.get<{ cnpj: string; empresa: string }[]>("/CategoriaDetalhe/empresas-by-group", {
                params: { group: grupo },
            });
            setEmpresasOptions(response.data.map(e => ({ value: e.cnpj, label: e.empresa })));
        } catch (error) {
            toast.error("Erro ao carregar as empresas do grupo.");
            console.error("Erro ao carregar as empresas do grupo:", error);
        }
    };

    const onFinish = async (values: FormValues) => {
        setIsLoading(true);

        if (!grupoDoCliente) {
            toast.error("Nome do grupo do cliente não disponível.");
            setIsLoading(false);
            return;
        }

        try {
            const nomeCategoria = values.CategoriaId;
            const categoriaExistente = categorias.find(
                (cat) => cat.categoria.toLowerCase() === nomeCategoria.toLowerCase()
            );

            const descricaoStr = values.Descricao?.join(',') || '';
            const excecaoStr = values.excecao?.join(',') || '';

            const payload = {
                // Adiciona o ID para a edição, se houver
                id: itemToEdit?.id || 0,
                Grupo: grupoDoCliente,
                Descricao: descricaoStr,
                Excecao: excecaoStr,
                categoriaName: nomeCategoria,
                CategoriaId: categoriaExistente ? parseInt(categoriaExistente.id) : 0,
                Pagamentos: values.Pagamentos ?? null,
                Empresas: values.Empresas ?? [],
            };

            // **NOVO:** Lógica condicional para POST ou PUT
            const endpoint = itemToEdit ? `/CategoriaDetalhe/update-category-detalhe` : '/CategoriaDetalhe/register-category-detalhe';
            const method = itemToEdit ? api.put : api.post;

            await method(endpoint, payload);

            const successMessage = itemToEdit ? 'Detalhe de categoria atualizado com sucesso!' : 'Detalhe de categoria cadastrado com sucesso!';
            toast.success(successMessage);

            // Reseta o formulário
            form.resetFields();
            onSuccess();

        } catch (error: any) {
            console.error('Erro ao processar o detalhe:', error);
            let errorMessage = 'Erro ao salvar. Tente novamente.';
            if (error.response) {
                errorMessage = `Erro do servidor: ${error.response.status} - ${error.response.data?.message || 'Resposta desconhecida'}`;
            } else if (error.request) {
                errorMessage = 'Erro de rede: O servidor não respondeu.';
            } else {
                errorMessage = `Erro na requisição: ${error.message}`;
            }
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const autoCompleteOptions = categorias.map(cat => ({ value: cat.categoria }));

    return (
        <div style={{ padding: '20px', margin: 'auto' }}>
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <>
                    <Form.Item
                        label="Digite uma categoria"
                        name="CategoriaId"
                        rules={[{ required: true, message: 'Por favor, selecione ou crie uma Categoria!' }]}
                    >
                        <AutoComplete
                            options={autoCompleteOptions}
                            placeholder="Digite para buscar ou criar uma nova"
                            filterOption={(inputValue, option) =>
                                option!.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        label="Contém na descrição do extrato"
                        name="Descricao"
                        rules={[{ required: true, message: 'Por favor, insira a Descrição!' }]}
                    >
                        <Select
                            mode="tags"
                            placeholder="Ex: Cielo"
                        />
                    </Form.Item>

                    <Form.Item
                        name='excecao'
                        label="Exceções"
                    >
                        <Select mode="tags" placeholder="Adicione palavras que NÃO PODEM estar na descrição" />
                    </Form.Item>

                    <Form.Item
                        name='Pagamentos'
                        label="Pagamentos"
                    >
                        <Select
                            allowClear
                            placeholder="Selecione a periodicidade"
                            options={pagamentosOptions}
                        />
                    </Form.Item>

                    <Form.Item
                        name='Empresas'
                        label="Empresas"
                    >
                        <EmpresaMultiSelect
                            options={empresasOptions}
                            placeholder="Todas as empresas do grupo"
                        />
                    </Form.Item>
                </>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={isLoading} style={{ width: '100%' }}>
                        {itemToEdit ? 'Atualizar Detalhe' : 'Salvar Detalhe'}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default CategoriaForm;